import { Box, Heading, Table, Tbody, Td, Th, Tr } from '@chakra-ui/react';
import { ParagraphPoiPassProfileFragment } from '../../../generated/types';
import React, { useContext } from 'react';
import EntityContext from '../../../context/EntityContext';
import { RatingStarsFixed } from '../../Common/RatingStars';
import nl2br from '../../../utils/nl2br';

interface PoiPassProfileProps {
  label?: string;
  subLabel?: string;
  data?: ParagraphPoiPassProfileFragment;
}

const offroadCarToLabel = (value: string) => {
  switch (value) {
    case 'motorcycle':
      return 'Motorrad';

    case 'suv':
      return 'Geländewagen';

    case 'truck':
      return '4x4 Truck';

    case 'camper':
      return '4x4 Camper';

    default:
      return value;
  }
};

const PoiPassProfile: React.FC<PoiPassProfileProps> = ({ label, data }) => {
  const entityContext = useContext(EntityContext);

  if (entityContext.entity.__typename !== 'Poi') {
    return null;
  }

  const poi = entityContext.entity;

  return (
    <Box className="ParagraphPoiPassProfile" width="100%">
      {(data?.title || label) && (
        <Heading mb={2} fontSize="2xl">
          {data?.title || label}
        </Heading>
      )}
      <Box width="100%">
        <Table
          width="100%"
          size="sm"
          __css={{
            th: {
              whiteSpace: 'nowrap',
              border: '1px solid',
              borderColor: '#ccc',
              bg: '#e6e6e6',
              px: {
                base: 2,
                md: 4,
              },
            },
            td: {
              width: '100%',
              border: '1px solid',
              borderColor: '#ccc',
              bg: '#eee',
              px: {
                base: 2,
                md: 4,
              },
            },
          }}
        >
          <Tbody>
            <Tr>
              <Th>Land</Th>
              <Td>{poi.country.name}</Td>
            </Tr>

            <Tr>
              <Th>Region</Th>
              <Td>{poi.region.name}</Td>
            </Tr>

            {poi.passHeight && (
              <Tr>
                <Th>Passhöhe</Th>
                <Td>{poi.passHeight} m</Td>
              </Tr>
            )}

            {poi.length > 0 && (
              <Tr>
                <Th>Länge</Th>
                <Td>{poi.length} km</Td>
              </Tr>
            )}

            {poi.maxSlope && (
              <Tr>
                <Th>Maximale Steigung</Th>
                <Td>{poi.maxSlope}</Td>
              </Tr>
            )}

            {poi.winterClosure && (
              <Tr>
                <Th>Wintersperre</Th>
                <Td>{poi.winterClosure}</Td>
              </Tr>
            )}

            {poi.basePlaces && (
              <Tr>
                <Th>Basisorte</Th>
                <Td>{poi.basePlaces}</Td>
              </Tr>
            )}

            {poi.location && (
              <Tr>
                <Th>Koordinaten</Th>
                <Td>
                  {poi.location.lat.toFixed(6)}, {poi.location.lng.toFixed(6)}
                </Td>
              </Tr>
            )}

            <Tr>
              <Th>Maut</Th>
              <Td>{poi.toll ? 'Ja' : 'Nein'}</Td>
            </Tr>

            {poi.toll && poi.tollPrices && (
              <Tr>
                <Th>Maut-Preise</Th>
                <Td>{nl2br(poi.tollPrices)}</Td>
              </Tr>
            )}

            <Tr>
              <Th>Belag</Th>
              <Td>
                {poi.unpaved
                  ? 'Enthält Abschnitte mit Schotter- oder Naturbelag'
                  : 'Geteert'}
              </Td>
            </Tr>

            {poi.numberOfTurns && (
              <Tr>
                <Th>Anzahl Kehren</Th>
                <Td>{poi.numberOfTurns}</Td>
              </Tr>
            )}

            <Tr>
              <Th>Sackgasse</Th>
              <Td>{poi.deadEnd ? 'Ja' : 'Nein'}</Td>
            </Tr>

            {poi.restrictions && (
              <Tr>
                <Th>Beschränkungen</Th>
                <Td>{nl2br(poi.restrictions)}</Td>
              </Tr>
            )}

            {poi.difficulty && (
              <Tr>
                <Th>Schwierigkeit</Th>
                <Td>{poi.difficulty.name}</Td>
              </Tr>
            )}

            {poi.stars && (
              <Tr>
                <Th>Sterne</Th>
                <Td>
                  <RatingStarsFixed value={poi.stars} />
                </Td>
              </Tr>
            )}

            {poi.offroadCars?.length > 0 && (
              <Tr>
                <Th>Fahrzeugkategorien</Th>
                <Td>{poi.offroadCars.map(offroadCarToLabel).join(', ')}</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default PoiPassProfile;
